import { appColors, BackrsIcon } from '@backrs_ui/common-ui';

import { Box, IconButton, SxProps, Theme } from '@mui/material';
import TextareaAutosize, { TextareaAutosizeProps } from 'react-textarea-autosize';

type EditMessageBoxProps = Readonly<{
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  isNumeric?: boolean;
}>;

export const EditMessageBox = ({
  value,
  onChange,
  onSubmit,
  isNumeric = false,
}: EditMessageBoxProps) => {
  return (
    <Box sx={{ width: '100%' }}>
      <TextareaAutosize
        value={value}
        onChange={(e) => {
          if (isNumeric) {
            const numericValue = e.target.value.replace(/[^0-9]/g, '');

            onChange(numericValue);
          } else {
            onChange(e.target.value);
          }
        }}
        onKeyPress={(e) => {
          if (isNumeric && !/[0-9]/.test(e.key) && e.key !== 'Enter') {
            e.preventDefault();
          }
          if (e.key === 'Enter') {
            e.preventDefault();
            onSubmit();
          }
        }}
        style={editableMessageTextarea}
        minRows={1}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <IconButton onClick={onSubmit} sx={editMessageSubmitButtonStyle}>
          Submit
        </IconButton>
      </Box>
    </Box>
  );
};

type UserMessageActionsButtonProps = Readonly<{
  onEdit: () => void;
}>;

export const UserMessageActionsButton = ({ onEdit }: UserMessageActionsButtonProps) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <IconButton
        onClick={onEdit}
        aria-label="edit message"
        sx={{
          padding: '12px 8px',
          '&:hover': {
            opacity: 0.5,
            backgroundColor: 'transparent',
          },
        }}
      >
        <BackrsIcon name="IconEdit" alt="Edit Message" style={editIconStyle} />
      </IconButton>
    </Box>
  );
};

const editableMessageTextarea: TextareaAutosizeProps['style'] = {
  width: '100%',
  padding: '8px 10px',
  margin: '0',
  marginTop: '10px',
  backgroundColor: appColors.color.blue10,
  borderRadius: '8px',
  border: 'none',
  outline: 'none',
  fontSize: '11pt',
  lineHeight: '20px',
  fontFamily: 'inherit',
  resize: 'none',
  boxSizing: 'border-box',
};

const editMessageSubmitButtonStyle: SxProps<Theme> = {
  color: appColors.neutral.gray1,
  padding: '0px',
  fontSize: '10pt',
  fontWeight: 'bold',
  '&:hover': {
    opacity: 0.5,
    backgroundColor: 'transparent',
  },
};

const editIconStyle: React.CSSProperties = {
  width: '20px',
  height: '20px',
  filter: 'grayscale(100%)',
  opacity: 0.3,
} as const;
